import {
  Badge,
  DropdownButton,
  DropdownButtonProps,
  FlexBox,
  Icon,
  SelectionList,
  SelectionListChangeEvent,
  Spinner,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import type {
  CustomAttributeFilterControl,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import orderBy from 'lodash/orderBy';
import {
  ElementRef,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import {
  CustomFieldAttribute,
  customFieldsApi,
  CustomFieldSelectAttribute,
  CustomFieldTextAttribute,
} from 'apis/customFields';

import {
  AnalyticsFilterItem,
  AnalyticsFilterLayout,
  AnalyticsFilterSearch,
} from '../../../filter';
import { AnalyticsFilterChangeCallback } from '../../../filter/analyticsFilter.types';

import styles from './analyticsCustomAttributesFilter.module.scss';
import { AnalyticsCustomAttributesFilterSelect } from './components/select/analyticsCustomAttributesFilterSelect';
import { AnalyticsCustomAttributesFilterText } from './components/text/analyticsCustomAttributesFilterText';

type AnalyticsCustomAttributesFilterProps = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** The filter field. */
  field: CustomAttributeFilterControl['field'];
  /** The currently selected custom attributes filter controls. */
  draftControls?: readonly CustomAttributeFilterControl[];
  /** The props to pass down to the Button component. */
  buttonProps?: DropdownButtonProps['buttonProps'];
  /** Callback when the filter is changed. */
  onChange: AnalyticsFilterChangeCallback<CustomAttributeFilterControl>;
};

export const AnalyticsCustomAttributesFilter = memo(
  function AnalyticsCustomAttributesFilter({
    panel,
    field,
    draftControls = [],
    buttonProps = {},
    onChange,
  }: AnalyticsCustomAttributesFilterProps): JSX.Element {
    const { dashboard } = useAnalyticsState(panel);

    const [isOpen, setIsOpen] = useState(false);
    const [activeAttribute, setActiveAttribute] =
      useState<CustomFieldAttribute | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    const searchRef = useRef<ElementRef<typeof AnalyticsFilterSearch>>(null);
    const listWrapperRef = useRef<HTMLDivElement>(null);

    const [readAttributesQuery, { attributes, isLoading, isUninitialized }] =
      customFieldsApi.useLazyReadAttributesQuery({
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          attributes: data?.data.attributes ?? [],
        }),
      });

    // Fetches the custom attributes the first time the dropdown is opened.
    useUpdateEffect(() => {
      if (isOpen && isUninitialized) {
        const preferCacheValue = true;
        readAttributesQuery({ entityId: 'conversation' }, preferCacheValue);
      }
    }, [isOpen, isUninitialized, readAttributesQuery]);

    /**
     * The custom attributes visible to the user.
     *
     * These are first filtered according to the search criteria if any, and
     * finally ordered so that deactivated attributes are at the bottom.
     */
    const visibleAttributes = useMemo(() => {
      const fuse = new Fuse<CustomFieldAttribute>(attributes, {
        keys: ['label', 'identifier'],
        threshold: 0.2,
        distance: 200,
      });

      const filteredAttributes = [
        ...(debouncedSearchTerm
          ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
          : attributes),
      ];

      return orderBy<CustomFieldAttribute>(
        filteredAttributes,
        'is_deactivated',
        'asc',
      );
    }, [attributes, debouncedSearchTerm]);

    const handleToggleDropdown = useCallback(
      (newIsOpen: boolean) => {
        setIsOpen(newIsOpen);

        if (newIsOpen) {
          ampli.analyticsFilterOpened({
            type: field,
            dashboard: dashboard.title,
            panel: panel?.title,
          });

          if (listWrapperRef.current) {
            listWrapperRef.current.scrollTop = 0;
          }
        } else {
          setActiveAttribute(null);
        }
      },
      [dashboard.title, field, panel?.title],
    );

    const handleChangeActiveAttribute = useCallback(
      ({ selectedItem }: SelectionListChangeEvent<CustomFieldAttribute>) => {
        setActiveAttribute(selectedItem.value);
      },
      [],
    );

    /** Returns the draft custom attribute filter control for a given attribute. */
    const getAttributeFilterControl = useCallback(
      (attributeId: string): CustomAttributeFilterControl | undefined =>
        draftControls.find(({ attribute_id }) => attribute_id === attributeId),
      [draftControls],
    );

    /** Handles rendering the list of custom attributes. */
    const renderAttributesList = useCallback(() => {
      const isEmpty = !visibleAttributes.length && !isLoading;

      const items = visibleAttributes.map((attribute, index, list) => {
        const isFirstDeactivatedItem =
          attribute.is_deactivated &&
          (index === 0 || !list[index - 1].is_deactivated);

        const draftControl = getAttributeFilterControl(attribute.id);
        const count = draftControl
          ? draftControl.values.length || 1
          : undefined;

        return (
          <Fragment key={attribute.identifier}>
            {isFirstDeactivatedItem && (
              <SelectionList.Divider label="Deactivated" />
            )}

            <AnalyticsFilterItem
              value={attribute}
              label={attribute.label}
              highlight={debouncedSearchTerm}
              noFixedSelectionStateSpace
              contentClassName={classNames(styles.itemContent, {
                [styles.itemContentWithBadge]: !!count,
              })}
              textProps={{
                color: attribute.is_deactivated ? 'tertiary' : undefined,
              }}
            >
              {count && (
                <Badge variant="secondary" size="small">
                  {count}
                </Badge>
              )}

              <Icon name="angle-right" />
            </AnalyticsFilterItem>
          </Fragment>
        );
      });

      return (
        <>
          <AnalyticsFilterLayout.Row borderBottom>
            <AnalyticsFilterSearch
              ref={searchRef}
              value={searchTerm}
              isOpen={isOpen}
              isDisabled={false}
              onChange={setSearchTerm}
            />
          </AnalyticsFilterLayout.Row>

          <AnalyticsFilterLayout.Row ref={listWrapperRef} noPadding scrollable>
            {isLoading && (
              <FlexBox
                alignItems="center"
                justifyContent="center"
                height="100%"
                padding="300"
              >
                <Spinner size={20} />
              </FlexBox>
            )}

            <SelectionList
              isActive={isOpen}
              selectedValues={activeAttribute ? [activeAttribute] : undefined}
              onChange={handleChangeActiveAttribute}
              equalityFn={(a, b) => a.identifier === b.identifier}
              defaultActiveItemIndex={0}
            >
              {items}
              {isEmpty && <AnalyticsFilterLayout.Empty />}
            </SelectionList>
          </AnalyticsFilterLayout.Row>
        </>
      );
    }, [
      activeAttribute,
      debouncedSearchTerm,
      getAttributeFilterControl,
      handleChangeActiveAttribute,
      isLoading,
      isOpen,
      searchTerm,
      visibleAttributes,
    ]);

    /** Handles rendering of the active attribute according to its type. */
    const renderAttributeType = useCallback(() => {
      if (!activeAttribute) return null;

      const draftControl = getAttributeFilterControl(activeAttribute.id);

      switch (activeAttribute.type) {
        case 'text':
          return (
            <AnalyticsCustomAttributesFilterText
              panel={panel}
              field={field}
              attribute={activeAttribute as CustomFieldTextAttribute}
              selectedOperator={draftControl?.operator}
              onChange={onChange}
              onBack={() => setActiveAttribute(null)}
            />
          );

        case 'select':
          return (
            <AnalyticsCustomAttributesFilterSelect
              panel={panel}
              field={field}
              attribute={activeAttribute as CustomFieldSelectAttribute}
              selectedValues={draftControl?.values.map(({ value }) => value)}
              selectedOperator={draftControl?.operator}
              isDropdownOpen={isOpen}
              onChange={onChange}
              onBack={() => setActiveAttribute(null)}
            />
          );
      }
    }, [activeAttribute, field, getAttributeFilterControl, isOpen, onChange]);

    return (
      <DropdownButton
        show={isOpen}
        dropdown={
          <AnalyticsFilterLayout>
            {activeAttribute ? renderAttributeType() : renderAttributesList()}
          </AnalyticsFilterLayout>
        }
        onToggle={handleToggleDropdown}
        buttonProps={{
          ...buttonProps,
          variant: 'tertiary',
          disabled: buttonProps.disabled || isLoading,
        }}
        data-analytics-filter={field}
        fixed
      >
        Attributes
      </DropdownButton>
    );
  },
);
